<template>
  <SectionSidebar :sections="sections" :variant="variant" />
</template>

<script setup lang="ts">
import type { SidebarSection } from "~/types/sidebar";

defineProps<{
  variant: "rail" | "full";
}>();

const meetingId = useMeetingId();
const hasModule = useModule();

const sections = computed<SidebarSection[]>(() => [
  {
    key: "config",
    titleKey: "label.parameters.section.config",
    items: [
      {
        key: "meeting",
        icon: "graduation-cap",
        labelKey: "label.parameters.meeting",
        to: RouteUtils.getMeetingParameters(meetingId.value!),
      },
      {
        key: "participants",
        icon: "person-circle-check",
        labelKey: "label.parameters.participants",
        to: RouteUtils.getMeetingParametersForParticipants(meetingId.value!),
      },
    ],
  },
  {
    key: "prep",
    titleKey: "label.parameters.section.prep",
    items: [
      {
        key: "teams",
        icon: "people-group",
        labelKey: "label.parameters.teams",
        to: RouteUtils.getMeetingParametersForTeams(meetingId.value!),
        visible: hasModule.value("team", meetingId.value),
      },
      {
        key: "dailytasks",
        icon: "list-check",
        labelKey: "label.parameters.daily-tasks",
        to: RouteUtils.getMeetingParametersForDailyTasks(meetingId.value!),
        visible: hasModule.value("dailytask", meetingId.value),
      },
      {
        key: "rooms",
        icon: "people-roof",
        labelKey: "label.parameters.rooms",
        to: RouteUtils.getMeetingParametersForRooms(meetingId.value!),
        visible: hasModule.value("event", meetingId.value),
      },
      {
        key: "planning",
        icon: "calendar",
        labelKey: "label.parameters.planning",
        to: RouteUtils.getMeetingParametersForPlanning(meetingId.value!),
        visible: hasModule.value("event", meetingId.value),
      },
      {
        key: "docs",
        icon: "folder-open",
        labelKey: "label.parameters.docs",
        to: RouteUtils.getMeetingParametersForLinks(meetingId.value!),
        visible: hasModule.value("resource", meetingId.value),
      },
      {
        key: "forms",
        icon: "clipboard-question",
        labelKey: "label.forms",
        to: RouteUtils.getMeetingFormsEdition(meetingId.value!),
        visible: hasModule.value("form", meetingId.value),
      },
      {
        key: "modularity",
        icon: "puzzle-piece",
        labelKey: "label.parameters.modularity",
        to: RouteUtils.getModularEventsEdition(meetingId.value!),
        visible: hasModule.value("modularity", meetingId.value),
      },
      {
        key: "communication",
        icon: "envelope",
        labelKey: "label.parameters.communication",
        to: RouteUtils.getMeetingParametersForCommunication(meetingId.value!),
        visible: hasModule.value("communication", meetingId.value),
      },
    ],
  },
  {
    key: "exports",
    titleKey: "label.parameters.section.exports",
    items: [
      {
        key: "exports",
        icon: "download",
        labelKey: "label.parameters.exports",
        to: RouteUtils.getMeetingParametersForExports(meetingId.value!),
      },
    ],
  },
  {
    key: "admin",
    titleKey: "label.parameters.section.admin",
    items: [
      {
        key: "opinion",
        icon: "feather",
        labelKey: "label.parameters.opinion-review",
        to: RouteUtils.getMeetingParametersForOpinionReview(meetingId.value!),
        // Même module que l'onglet « Avis » de la fiche stagiaire, qui sert à
        // les rédiger : relire des avis qu'on ne peut pas écrire n'a pas de sens.
        visible: hasModule.value("participant_comment", meetingId.value),
      },
      {
        key: "tagada",
        icon: "rotate",
        labelKey: "label.parameters.tagadasync",
        to: RouteUtils.getMeetingParametersForTagada(meetingId.value!),
        visible: hasModule.value("tagada", meetingId.value),
      },
      {
        key: "formers",
        icon: "chalkboard-user",
        labelKey: "label.parameters.formers",
        to: RouteUtils.getMeetingParametersForFormers(meetingId.value!),
      },
    ],
  },
]);
</script>
